export const DEFAULT_REVENUE_LEVERS_INPUTS = {
  leads: 100,
  conv: 20,
  check: 15000,
  freq: 1,
  target: 450000,
};

export const DEFAULT_SLIDER_VALS = {
  l: 15,
  c: 15,
  p: 15,
  q: 15,
};

export const LEVER_KEYS = ['l', 'c', 'p', 'q'];

export const LEVER_REC_KEYS = {
  l: 'REC_LEADS',
  c: 'REC_CONV',
  p: 'REC_CHECK',
  q: 'REC_FREQ',
};

export const HOW_STEPS = [
  { num: '1', title: 'Каждый пункт имеет вес.', text: '«Да» = 100% балла, «Частично» = 50%, «Нет» = 0%. Итог — сумма взвешенных баллов.' },
  { num: '2', title: 'Пороги готовности:', text: 'до 50% — рано, 50–79% — доработать, 80%+ — можно запускать тест.' },
  { num: '3', title: 'Список «Сделать в первую очередь»', text: 'показывает пункты с ответом «Нет» и «Частично» по приоритету веса.' },
  { num: '4', title: 'Стратегия:', text: 'найдите рычаг с наименьшим % изменения — это самый быстрый путь. Но оптимальный сценарий — небольшой рост по всем четырём одновременно.' },
];

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return min;
  }

  return Math.min(max, Math.max(min, parsed));
};

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${Math.round(value).toLocaleString(locale)} ₸`;
};

export const formatCount = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return Math.round(value).toLocaleString(locale);
};

export const formatPercentPlain = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${value.toFixed(digits)}%`;
};

export const formatSignedPercent = (value) => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

export const getEffortLevel = (pct) => {
  const abs = Math.abs(pct);

  if (abs <= 30) {
    return 'low';
  }

  if (abs <= 60) {
    return 'mid';
  }

  return 'high';
};

export const calculateRevenueMetrics = (inputs) => {
  const leads = clampNumber(inputs.leads, 1);
  const conv = clampNumber(inputs.conv, 0.1, 100) / 100;
  const check = clampNumber(inputs.check, 1);
  const freq = clampNumber(inputs.freq, 0.1);
  const target = clampNumber(inputs.target, 1);

  const revenue = leads * conv * check * freq;
  const clients = leads * conv;
  const deals = clients * freq;
  const targetValid = target > revenue;

  const growthPct = targetValid ? ((target - revenue) / revenue) * 100 : null;
  const growthAbs = targetValid ? target - revenue : null;

  const formulaDetail = `${formatCount(leads)} × ${formatPercentPlain(conv * 100)} × ${Math.round(check).toLocaleString('ru-RU')} ₸ × ${freq.toFixed(1)}`;

  return {
    leads,
    conv,
    check,
    freq,
    target,
    revenue,
    clients,
    deals,
    targetValid,
    growthPct,
    growthAbs,
    formulaDetail,
  };
};

export const buildSingleLevers = (metrics) => {
  const { leads, conv, check, freq, target } = metrics;
  const C = conv;
  const L = leads;
  const P = check;
  const Q = freq;

  const levers = [
    {
      key: 'l',
      name: 'Лиды',
      desc: 'входящие обращения',
      curVal: L,
      needVal: target / (C * P * Q),
      formatNeed: (v) => `${formatCount(v)}`,
      formatAbs: (need) => `+${formatCount(need - L)}`,
    },
    {
      key: 'c',
      name: 'Конверсия',
      desc: 'лид → сделка',
      curVal: C * 100,
      curFmt: formatPercentPlain(C * 100),
      needVal: Math.min((target / (L * P * Q)) * 100, 100),
      formatNeed: (v) => `${v.toFixed(1)}%`,
      formatAbs: (need) => `+${(need - C * 100).toFixed(1)} п.п.`,
    },
    {
      key: 'p',
      name: 'Средний чек',
      desc: 'сумма одной продажи',
      curVal: P,
      curFmt: formatMoney(P),
      needVal: target / (L * C * Q),
      formatNeed: (v) => formatMoney(v),
      formatAbs: (need) => `+${formatMoney(need - P)}`,
    },
    {
      key: 'q',
      name: 'Покупок на клиента',
      desc: 'частота покупок',
      curVal: Q,
      needVal: target / (L * C * P),
      formatNeed: (v) => v.toFixed(2),
      formatAbs: (need) => `+${(need - Q).toFixed(2)}`,
    },
  ];

  const rows = levers.map((lever) => {
    const need = lever.needVal;
    const valid = Number.isFinite(need) && need > 0;
    const deltaPct = valid ? ((need - lever.curVal) / lever.curVal) * 100 : null;

    return {
      ...lever,
      need,
      valid,
      deltaPct,
      effort: valid ? getEffortLevel(deltaPct) : null,
    };
  });

  const pcts = rows.map((row) =>
    row.valid ? ((row.need - row.curVal) / row.curVal) * 100 : Infinity,
  );
  const finitePcts = pcts.filter((pct) => Number.isFinite(pct));
  const minPct = finitePcts.length ? Math.min(...finitePcts) : null;
  const bestIdx = minPct !== null ? pcts.indexOf(minPct) : -1;

  return rows.map((row, index) => ({
    ...row,
    isBest: index === bestIdx,
  }));
};

export const calculateCombined = (metrics, sliderVals) => {
  const { leads, conv, check, freq, target } = metrics;
  const L = leads;
  const C = conv;
  const P = check;
  const Q = freq;

  const defs = [
    { key: 'l', name: 'Лиды', desc: 'входящие обращения', cur: L, unit: 'шт' },
    { key: 'c', name: 'Конверсия', desc: 'лид → сделка', cur: C * 100, unit: '%' },
    { key: 'p', name: 'Средний чек', desc: 'сумма одной продажи', cur: P, unit: '₸' },
    { key: 'q', name: 'Покупок на клиента', desc: 'частота покупок', cur: Q, unit: 'раз' },
  ];

  const rows = defs.map((def) => {
    const pct = clampNumber(sliderVals[def.key], 0, 100);
    const newVal =
      def.key === 'c' ? Math.min(def.cur * (1 + pct / 100), 100) : def.cur * (1 + pct / 100);

    return {
      ...def,
      pct,
      newVal,
      formatNew:
        def.key === 'c'
          ? `${newVal.toFixed(1)}%`
          : def.key === 'p'
            ? formatMoney(newVal)
            : def.key === 'q'
              ? `${newVal.toFixed(2)} раз`
              : `${formatCount(newVal)} шт`,
    };
  });

  const kL = 1 + sliderVals.l / 100;
  const kC = C > 0 ? Math.min(C * (1 + sliderVals.c / 100), 1) / C : 1;
  const kP = 1 + sliderVals.p / 100;
  const kQ = 1 + sliderVals.q / 100;
  const newRevenue = L * kL * (C * kC) * P * kP * Q * kQ;
  const progress = target > 0 ? Math.min((newRevenue / target) * 100, 110) : 0;
  const reached = newRevenue >= target;

  return {
    rows,
    newRevenue,
    progress: Math.min(progress, 100),
    reached,
    progressLabelPct: target > 0 ? Math.round((newRevenue / target) * 100) : 0,
  };
};
